import React, { useEffect, useRef, useState } from "react";
import { RCCAlert, RCCButton, RCCGrid, RCCHookToast, RCCIcon, RCCPopover, RCCServicesApi, RCCTooltip, arrayMove } from "react-common-components";
import Loader from "src/components/Loader";
import Title from "src/components/Title";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import SortableContainer from "src/components/Sortable/Container";
import SortableItem from "src/components/Sortable/Item";
import ModalLevel2, { IRefModalLevel2 } from "src/pages/Analysis/Panel/Components/ModalLevel2";
import CheckPermission from "src/components/CheckPermission";
import { PermissionEnum } from "src/context/Auth/interface";
import { simplePermissionType } from "src/components/Permissions/interface";
import { restrictToVerticalAxis, restrictToWindowEdges } from "src/components/Sortable/Container/modifiers";
import { IMatba001Form, IMatba002 } from "./interface";
import ModalLevel1, { IRefModalLevel1 } from "./Components/ModalLevel1";
import ListOfLevel1 from "./Components/listOfLevel1";
import ModalRadarChart, { IRefModalRadarChart } from "./Components/modalRadarChart";

interface IAnalysisContent{
	modelSelected: IMatba001Form
}

const AnalysisContent: React.FC<IAnalysisContent> = ({
	modelSelected
}) => {

	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();

	const refModalLevel1 = useRef<IRefModalLevel1>(null);
	const refModalLevel2 = useRef<IRefModalLevel2>(null);
	const refModalRadarChart = useRef<IRefModalRadarChart>(null);

	const [ data, setData] = useState<IMatba002[] | null>(null);
	const [ tempData, setTempData] = useState<IMatba002[] | null>(null);
	const [loading, setIsLoading] = useState(true);
	const [waitingForResponse, setWaitingForResponse] = useState(false);

	const loadData = async () => {
		try{
			setIsLoading(true);
			const response = await RCCServicesApi.getRequest({
				method: "Analysis/ListLevel1",
				urlParameters: {
					id: modelSelected?.maa001Id
				}
			});
			setData(response.data);
			setIsLoading(false);
		}catch(err){
			standardErrorMessages(err, translate("Erro ao buscar os dados"), addToast);
			setIsLoading(false);
		}
	};

	const onCallbackChangeLevel1 = (level1: IMatba002) => {
		setData(data && data.map((item) => {
			if(item.maa002Id === level1.maa002Id){
				return {
					...level1
				};
			}
			return item;
		}));
	};

	const onCallbackDelete = (id: number) => {
		setData((prevState) => prevState && prevState.filter((item) => item.maa002Id !== id));
	};

	const queryReorderLevel1 = async (itemslevel1: any[]) => {
		setWaitingForResponse(true);
		try{
			await RCCServicesApi.putRequest({
				method: "Analysis/UpdateOrderLevel1",
				body: {
					rows: itemslevel1
				}
			});
			setWaitingForResponse(false);
			
			addToast({
				color: "green",
				description: translate("{{label}} movido(a) com sucesso", {label: modelSelected.maa001Label1}),
				title: translate("Sucesso")
			});

			return true;
		}catch(err){
			setData(tempData);
			standardErrorMessages(err, translate("Erro ao mover {{label}}", {label: modelSelected.maa001Label1}), addToast);
			setWaitingForResponse(false);
			return false;
		}
	};

	useEffect(() => {
		loadData();
	}, [modelSelected]);

	return (
		<>
			<RCCGrid
				container 
				item
				justifyContent="space-between"
				xs={12}
			>
				<Title color="purple" size="xl">
					{modelSelected.maa001Titulo}
				</Title>
				
				<RCCGrid container item xs={4} justifyContent="flex-end">
					<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
						{modelSelected.userPermission > Number(simplePermissionType.VIEW) && modelSelected.maa001Adicionanivel1 === "S" && (
							<RCCTooltip
								text={translate("Adicionar {{label}}", {
									label: modelSelected.maa001Label1
								})}
								position="leftEnd"
								color="purple"
							>
								<RCCButton
									icon={{name: "plus", color: "#FFF", size: 18}}
									color="purple"
									layout="isRounded"
									onClick={() => {refModalLevel1.current?.actionButtonAdd(modelSelected.maa001Id, modelSelected.maa001Label1);}}
									size="md"
								/>
							</RCCTooltip>
						)}
					</CheckPermission>
					{
						modelSelected.maa001Graficoradar === "S" && (
							<RCCTooltip
								text={translate("Visualizar Gráfico", {
									label: modelSelected.maa001Label1
								})}
								position="leftEnd"
								color="blue"
							>
								<RCCButton
									icon={{name: "chartBar", color: "#FFF", size: 18}}
									color="blue"
									layout="isRounded"
									onClick={() => {refModalRadarChart.current?.openModal(modelSelected);}}
									size="md"
								/>
							</RCCTooltip>
						)
					}
				</RCCGrid>
				
			</RCCGrid>
			<RCCGrid 
				container
				item
			>
				<Title 
					size="xs"
					color="grey"
				>
					{modelSelected.maa001Label1} - {modelSelected.maa001Label2} - {modelSelected.maa001Label3}
				</Title>
				<RCCPopover
					position="bottom"
					header={{title:translate("Descrição")}}
					body={{text:`${modelSelected.maa001Descricao}`}}
				>
					<RCCIcon
						name="infoCircle"
						color="grey"
					/>
				</RCCPopover>
			</RCCGrid>
			
			{loading ? (
				<Loader />
			) : (
				<>
					<RCCGrid styleInline="margin-top: 10px">
						{!data || data.length === 0 ? 
							(
								<RCCGrid 
									container 
									styleInline="margin: 10px"
								>
									<RCCAlert 
										title={translate("Atenção")}
										description={translate("Nenhum(a) {{label}} encontrado(a)", {
											label1: modelSelected.maa001Label1
										})}
									/>
								</RCCGrid>
							) : (
								<>
									<SortableContainer
										onDragStart={() => {
											setTempData(data);
										}}
  									onDragEnd={(activeIdX, overId) => {
											if(activeIdX === overId)
												return; 	

											const activeIndex = data.findIndex((checks) => checks.id === activeIdX);
											const overIndex = data.findIndex((checks) => checks.id === overId);

											const orderedItems = arrayMove(data, activeIndex, overIndex);

											setData(orderedItems);

											const rowsOrdered: any[] = [];

											orderedItems.forEach((item: any, index) => {
												const row = {
													maa002Id: item.maa002Id,
													maa002Ordem: index
												};
												rowsOrdered.push(row);
											});

											queryReorderLevel1(rowsOrdered);
										}}
										items={data.map((line) => ({
											...line,
											id: `${line.maa002Id.toString()}`
										}))}
										modifiers={[restrictToWindowEdges, restrictToVerticalAxis]}
  								>
										{data?.map((level1) => (
											<div key={`container-level1-${level1.maa002Id}`}>
												<SortableItem 
													id={level1.maa002Id.toString()} 
													key={level1.maa002Id} 
													disabled={waitingForResponse}
												>
													<ListOfLevel1 
														analysisData={modelSelected}
														level1Data={level1}
														refModalLevel1={refModalLevel1}
														refModalLevel2={refModalLevel2}
													/>
												</SortableItem>
											</div>
										))}
									</SortableContainer>
								</>
							)
						}
					</RCCGrid>
				</>
			)}

			<ModalLevel1
				onCallbackAdd={loadData}
				onCallbackChange={onCallbackChangeLevel1}
				onCallbackDelete={onCallbackDelete}
				ref={refModalLevel1}
			/>

			<ModalLevel2
				ref={refModalLevel2}
			/>

			<ModalRadarChart 
				ref={refModalRadarChart} />

		</>
	);
};


export default AnalysisContent;
